import { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Building2, CreditCard, AlertTriangle, ArrowRight,
  TrendingDown, TrendingUp, CheckCircle2, Clock
} from 'lucide-react';
import PageHeader from '../../components/ui/PageHeader';
import Card, { CardHeader } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Avatar from '../../components/ui/Avatar';
import { formatMoneyCompact, formatMoney } from '../../utils/formatters';
import { useShop } from '../../hooks/useShop';

// ─── Mock data (tied to real shop IDs where possible) ───

const SHOP_METRICS = [
  { id: 7103, name: 'Montgomery',  region: 'MTE-4', exposure: 329_000, issues: 6839, trend: -4.2, resolved: 42 },
  { id: 5622, name: 'Spring',      region: 'Yes!',  exposure: 109_000, issues: 12380, trend: 1.8, resolved: 28 },
  { id: 5923, name: 'Porter',      region: 'Yes!',  exposure: 224_000, issues: 4375, trend: -8.5, resolved: 51 },
  { id: 5814, name: 'Pinehurst',   region: 'Yes!',  exposure: null,     issues: null,  trend: null,  resolved: null, timeout: true },
  { id: 6783, name: 'Yes! Auto',   region: 'Yes!',  exposure: 67_400,  issues: 2103, trend: -2.1, resolved: 17 },
  { id: 6678, name: 'MT&AC-2',     region: 'MTE',   exposure: 48_200,  issues: 1587, trend: -1.3, resolved: 12 },
  { id: 5623, name: 'MT&AC-5',     region: 'MTE',   exposure: 82_900,  issues: 2847, trend: 0.4,  resolved: 19 },
];

const STATEMENT_QUEUE = [
  { vendor: 'XL Parts',           period: '04/07 – 04/13', amount: 4872.50, status: 'ready', shops: 7 },
  { vendor: 'Advance Auto Parts', period: '04/07 – 04/13', amount: 1963.22, status: 'ready', shops: 5 },
  { vendor: 'O\'Reilly First Call', period: '04/07 – 04/13', amount: 7103.15, status: 'reviewed', shops: 7 },
  { vendor: 'AutoZone Pro',       period: '04/07 – 04/13', amount: 892.40,  status: 'reviewing', shops: 4 },
];

const ESCALATIONS = [
  {
    id: 1,
    from: 'Nadeem Nisar',
    subject: 'Advance Level pricing mismatch — 6 items at Montgomery',
    store: 'Montgomery',
    assignee: 'Patricia Gray',
    days_open: 2,
    amount: 1700,
  },
  {
    id: 2,
    from: 'Nadeem Nisar',
    subject: 'Transtar core return sitting 9 days past SLA',
    store: 'Spring',
    assignee: 'Store Manager',
    days_open: 2,
    amount: 489,
  },
];

// ─── Main page ───

export default function SuperAdminDashboardPage() {
  const { dashboard, loading } = useOutletContext();
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const totals = useMemo(() => {
    const exposure = SHOP_METRICS.reduce((s, x) => s + (x.exposure || 0), 0);
    const issues = SHOP_METRICS.reduce((s, x) => s + (x.issues || 0), 0);
    const resolved = SHOP_METRICS.reduce((s, x) => s + (x.resolved || 0), 0);
    return { exposure, issues, resolved };
  }, []);

  const achTotal = STATEMENT_QUEUE.filter((s) => s.status === 'ready').reduce((s, x) => s + x.amount, 0);

  return (
    <div>
      <PageHeader
        title={<>Good morning, <span className="text-[var(--color-warm-900)]">Danish</span></>}
        description={`${today} · Org-wide view across all 7 shops`}
        actions={
          <>
            <Button variant="ghost">Download report</Button>
            <Button variant="primary" leftIcon={<CreditCard size={16} />}>Run ACH</Button>
          </>
        }
      />

      {/* Hero metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 stagger-children">
        <Hero
          label="Total Open Exposure"
          value={formatMoneyCompact(totals.exposure)}
          sub={`${totals.issues.toLocaleString()} open items`}
          accent="warm"
          trend={-3.2}
          trendLabel="vs last month"
        />
        <Hero
          label="Ready for ACH"
          value={formatMoneyCompact(achTotal)}
          sub={`${STATEMENT_QUEUE.filter(s => s.status === 'ready').length} statements`}
          accent="action"
          icon={<CreditCard />}
        />
        <Hero
          label="Resolved This Month"
          value={totals.resolved}
          sub={formatMoneyCompact(48_300)}
          accent="success"
          trend={12.4}
          trendLabel="vs last month"
        />
        <Hero
          label="Escalations to You"
          value={ESCALATIONS.length}
          sub="From auditor"
          accent="danger"
          icon={<AlertTriangle />}
        />
      </div>

      {/* Shop comparison grid */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[15px] font-bold text-[var(--color-warm-900)] tracking-tight">
            All shops
          </h2>
          <Button variant="ghost" size="sm" rightIcon={<ArrowRight size={14} />}>
            Compare
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {SHOP_METRICS.map((shop) => (
            <ShopCard key={shop.id} shop={shop} />
          ))}
        </div>
      </div>

      {/* Two-column: ACH queue + Escalations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <StatementQueueCard queue={STATEMENT_QUEUE} />
        <EscalationsCard items={ESCALATIONS} />
      </div>
    </div>
  );
}

// ─── Sub components ───

function Hero({ label, value, sub, accent = 'warm', icon, trend, trendLabel }) {
  const accents = {
    warm:     'bg-[var(--color-warm-900)] text-[var(--color-cream-50)] border-[var(--color-warm-900)]',
    recovery: 'bg-[var(--color-recovery-50)] border-[var(--color-recovery-100)]',
    action:   'bg-[var(--color-action-50)] border-[var(--color-action-100)]',
    insight:  'bg-[var(--color-insight-50)] border-[var(--color-insight-100)]',
    danger:   'bg-[var(--color-danger-50)] border-[var(--color-danger-100)]',
    success:  'bg-[var(--color-success-50)] border-[var(--color-success-100)]',
  };
  const isDark = accent === 'warm';
  const labelColor = isDark ? 'text-[var(--color-warm-300)]' : 'text-[var(--color-warm-500)]';
  const valueColor = isDark ? 'text-[var(--color-cream-50)]' : 'text-[var(--color-warm-900)]';
  const subColor = isDark ? 'text-[var(--color-warm-200)]' : 'text-[var(--color-warm-600)]';

  return (
    <div className={`p-5 rounded-[var(--radius-lg)] border ${accents[accent]} shadow-[var(--shadow-sm)]`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`text-[11px] font-bold uppercase tracking-[0.8px] ${labelColor}`}>
          {label}
        </div>
        {icon && (
          <div className={`${isDark ? 'text-[var(--color-warm-300)]' : 'text-[var(--color-warm-500)]'} [&_svg]:w-[18px] [&_svg]:h-[18px]`}>
            {icon}
          </div>
        )}
      </div>
      <div className={`text-[32px] font-extrabold tracking-tight tabular-nums leading-none mb-1 ${valueColor}`}>
        {value}
      </div>
      <div className={`text-[12px] ${subColor} tabular-nums flex items-center gap-2`}>
        <span>{sub}</span>
        {trend != null && (
          <span className={`inline-flex items-center gap-0.5 font-semibold ${
            trend < 0
              ? isDark ? 'text-[var(--color-success-300)]' : 'text-[var(--color-success-600)]'
              : isDark ? 'text-[var(--color-action-300)]' : 'text-[var(--color-action-600)]'
          }`}>
            {trend < 0 ? <TrendingDown size={11} /> : <TrendingUp size={11} />}
            {Math.abs(trend).toFixed(1)}%
          </span>
        )}
      </div>
    </div>
  );
}

function ShopCard({ shop }) {
  if (shop.timeout) {
    return (
      <div className="p-4 rounded-[var(--radius-md)] bg-[var(--color-cream-50)] border border-[var(--color-warm-100)]">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.8px] text-[var(--color-warm-500)]">
              {shop.region}
            </div>
            <div className="text-[15px] font-bold text-[var(--color-warm-900)]">{shop.name}</div>
          </div>
          <Badge tone="action" size="xs">Timeout</Badge>
        </div>
        <div className="text-[11px] text-[var(--color-warm-500)]">
          Query exceeded limits. Needs pagination.
        </div>
      </div>
    );
  }
  const trendDown = shop.trend < 0;
  return (
    <div className="p-4 rounded-[var(--radius-md)] bg-[var(--color-cream-50)] border border-[var(--color-warm-100)] hover:shadow-[var(--shadow-sm)] hover:border-[var(--color-warm-200)] transition-all cursor-pointer">
      <div className="flex items-center justify-between mb-2">
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-[0.8px] text-[var(--color-warm-500)] truncate">
            {shop.region}
          </div>
          <div className="text-[15px] font-bold text-[var(--color-warm-900)] truncate">
            {shop.name}
          </div>
        </div>
        <div className={`text-[11px] font-semibold tabular-nums shrink-0 inline-flex items-center gap-0.5 ${
          trendDown ? 'text-[var(--color-success-600)]' : 'text-[var(--color-action-600)]'
        }`}>
          {trendDown ? <TrendingDown size={11} /> : <TrendingUp size={11} />}
          {Math.abs(shop.trend).toFixed(1)}%
        </div>
      </div>
      <div className="text-[20px] font-extrabold text-[var(--color-warm-900)] tabular-nums leading-none mb-1">
        {formatMoneyCompact(shop.exposure)}
      </div>
      <div className="text-[11px] text-[var(--color-warm-500)] tabular-nums">
        {shop.issues.toLocaleString()} issues · {shop.resolved} resolved
      </div>
    </div>
  );
}

function StatementQueueCard({ queue }) {
  const total = queue.filter((s) => s.status === 'ready').reduce((s, x) => s + x.amount, 0);
  return (
    <Card padded={false}>
      <div className="p-5">
        <CardHeader
          title={
            <span className="flex items-center gap-2.5">
              <CreditCard size={18} className="text-[var(--color-action-500)]" />
              ACH Payment Queue
              <Badge tone="neutral" size="xs">Preview</Badge>
            </span>
          }
          description="Vendor statements ready for your ACH payment this week"
          action={<Button variant="action" size="sm">Pay all ready</Button>}
        />
        <div className="text-[12px] text-[var(--color-warm-500)] mb-2">
          <span className="font-semibold text-[var(--color-warm-900)] tabular-nums">{formatMoney(total)}</span>
          {' '}total ready to pay
        </div>
      </div>
      <ul className="divide-y divide-[var(--color-warm-100)]">
        {queue.map((s, i) => (
          <li key={i} className="px-5 py-3.5 hover:bg-[var(--color-cream-100)] transition-colors">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-[var(--radius-sm)] flex items-center justify-center text-[12px] font-bold shrink-0 ${
                s.status === 'ready'    ? 'bg-[var(--color-success-100)] text-[var(--color-success-600)]'
                : s.status === 'reviewing' ? 'bg-[var(--color-action-100)] text-[var(--color-action-600)]'
                : 'bg-[var(--color-cream-200)] text-[var(--color-warm-600)]'
              }`}>
                {s.vendor.slice(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-[var(--color-warm-900)] truncate">{s.vendor}</div>
                <div className="text-[11px] text-[var(--color-warm-500)]">
                  {s.period} · {s.shops} shops
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[13px] font-semibold text-[var(--color-warm-900)] tabular-nums">
                  ${s.amount.toFixed(2)}
                </div>
                <Badge
                  tone={s.status === 'ready' ? 'success' : s.status === 'reviewing' ? 'action' : 'neutral'}
                  size="xs"
                  className="mt-1"
                >
                  {s.status}
                </Badge>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function EscalationsCard({ items }) {
  return (
    <Card padded={false}>
      <div className="p-5">
        <CardHeader
          title={
            <span className="flex items-center gap-2.5">
              <AlertTriangle size={18} className="text-[var(--color-danger-500)]" />
              Escalations From Auditor
              <Badge tone="neutral" size="xs">Preview</Badge>
            </span>
          }
          description="Items the auditor flagged for your attention — stores not resolving on time"
          action={<Button variant="ghost" size="sm" rightIcon={<ArrowRight size={14} />}>View all</Button>}
        />
      </div>
      {items.length === 0 ? (
        <div className="px-5 pb-6 text-center">
          <CheckCircle2 size={36} className="text-[var(--color-success-500)] mx-auto mb-2" />
          <div className="text-[13px] font-semibold text-[var(--color-warm-900)]">All caught up</div>
          <div className="text-[11px] text-[var(--color-warm-500)]">No escalations from the auditor today.</div>
        </div>
      ) : (
        <ul className="divide-y divide-[var(--color-warm-100)]">
          {items.map((e) => (
            <li key={e.id} className="px-5 py-3.5 hover:bg-[var(--color-cream-100)] transition-colors cursor-pointer">
              <div className="flex items-start gap-3">
                <Avatar name={e.from} size="sm" />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold text-[var(--color-warm-900)] leading-snug">
                    {e.subject}
                  </div>
                  <div className="text-[11px] text-[var(--color-warm-500)] mt-1 flex items-center gap-2 flex-wrap">
                    <span>{e.store}</span>
                    <span className="text-[var(--color-warm-300)]">·</span>
                    <span>Assignee: {e.assignee}</span>
                    <span className="text-[var(--color-warm-300)]">·</span>
                    <span className="inline-flex items-center gap-1 text-[var(--color-danger-600)] font-semibold">
                      <Clock size={11} />
                      {e.days_open}d open
                    </span>
                  </div>
                </div>
                <div className="text-[13px] font-bold text-[var(--color-warm-900)] tabular-nums shrink-0">
                  ${e.amount}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
